import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import {
  CadastralParcelDto,
  CadastralParcelFeatureDto,
  PropertyCadastralDto,
  PropertyId,
} from '@workspace/data-types';
import { PropertyCadastralMapComponent } from './cadastral-map.component';

@Component({
  selector: 'etxe-property-cadastral-card, etxe-property-cadastral-display',
  imports: [CommonModule, MatExpansionModule, MatIconModule, PropertyCadastralMapComponent],
  templateUrl: './cadastral-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyCadastralCardComponent {
  // Inputs & Models
  cadastral = input<PropertyCadastralDto | null | undefined>();
  addressLabel = input<string | null | undefined>();
  cityCode = input<string | null | undefined>();
  coordinates = input<{ latitude?: number; longitude?: number } | null | undefined>();
  isOwner = input<boolean>(true);
  propertyId = input<PropertyId | null | undefined>();
  expanded = model<boolean>(true);
  variant = input<'flat' | 'card'>('flat');

  parcels = computed<CadastralParcelDto[]>(() => this.cadastral()?.parcels ?? []);

  totalSurface = computed<number | undefined>(() => {
    const cad = this.cadastral();
    if (cad?.totalSurface != null) return cad.totalSurface;
    if (this.parcels().length > 0) {
      return this.parcels().reduce((sum, p) => sum + (p.surface || 0), 0);
    }
    return undefined;
  });

  selectedReferences = computed<string[]>(() => this.parcels().map((p) => p.reference));

  initialParcelFeatures = computed<CadastralParcelFeatureDto[]>(() => {
    return this.parcels()
      .filter((p) => Boolean(p.geometry))
      .map((p) => ({
        id: p.reference,
        section: p.section || '',
        numero: p.numero || '',
        surface: p.surface,
        hasBdnbBuilding: p.hasBdnbBuilding,
        geometry: p.geometry,
      }));
  });

  hasData = computed(() => Boolean(this.parcels().length > 0 || this.totalSurface() != null));

  primaryParcel = computed<CadastralParcelDto | null>(() => {
    const list = this.parcels();
    return list.find((p) => p.isPrimary) ?? list[0] ?? null;
  });

  hasCoordinates = computed(() =>
    Boolean(this.coordinates()?.latitude != null && this.coordinates()?.longitude != null),
  );
}

export { PropertyCadastralCardComponent as PropertyCadastralDisplayComponent };
